<template>
  <form
    @submit.prevent="submit"
    class="product-family-revision-form uk-form-stacked uk-grid-small"
  >
    <h2 class="uk-margin-medium">
      <router-link
        :to="{ name: 'product-family', params: { id: productFamily.id } }"
      >{{productFamily.name}}</router-link>
    </h2>

    <!-- Validity -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.validity')}}</legend>
      <div class="uk-grid-small" uk-grid>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validFrom"
            :label="$t('fields.validFrom')"
            :max="fields.values.validUntil"
            placeholder="∞"
            v-model="fields.values.validFrom"
          />
        </div>
        <div class="uk-width-1-4">
          <date-time-picker
            :disabled="!isEditable"
            :errors="fields.errors.validUntil"
            :label="$t('fields.validUntil')"
            :min="fields.values.validFrom"
            placeholder="∞"
            v-model="fields.values.validUntil"
          />
        </div>
      </div>
    </fieldset>

    <!-- Categories -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.categories')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.categories"
      >
        <product-category-assignment-manager
          :disabled="!isEditable"
          :errors="fields.errors.categories"
          :value="fields.values.categories"
          @change="fields.change.categories"
        />

        <div slot="inherited">
          <product-category-assignment-table
            :product-categories="this.productFamily.categories"
            :show-actions="false"
          />
        </div>
      </inheritance-toggle>
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.altName"
        :label="$t('fields.altName')"
        :placeholder="productFamily.altName"
        v-model="fields.values.altName"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.usps"
        :label="$t('fields.usps')"
        :placeholder="productFamily.usps"
        v-model="fields.values.usps"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.summaryTexts"
        :label="$t('fields.summaryTexts')"
        :placeholder="productFamily.summaryTexts"
        v-model="fields.values.summaryTexts"
      />
    </fieldset>

    <!-- Manufacturer Contacts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{ $t('fieldsets.manufacturerContact') }}</legend>
      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.manufacturerContact"
        :label="$t('fields.manufacturerContact')"
        :placeholder="productFamily.manufacturerContact"
        rows="5"
        v-model="fields.values.manufacturerContact"
      />
      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.manufacturerContactEu"
        :label="$t('fields.manufacturerContactEu')"
        :placeholder="productFamily.manufacturerContactEu"
        rows="5"
        v-model="fields.values.manufacturerContactEu"
      />
    </fieldset>


    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.seo')}}</legend>

      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.description"
        :label="$t('fields.description')"
        :placeholder="productFamily.description"
        :rows="5"
        v-model="fields.values.description"
      />
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.legalTexts')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.legalTexts"
      >
        <legal-text-manager
          :disabled="!isEditable"
          :errors="fields.errors.legalTexts"
          v-model="fields.values.legalTexts"
        />
        <legal-text-manager
          :has-controls="false"
          :value="productFamily.legalTexts"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Documents -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.documents')}}</legend>

      <inheritance-toggle
        :overridden-default="[]"
        toggle-label="Überschreiben"
        v-model="fields.values.documents"
      >
        <attachment-manager
          :disabled="!isEditable"
          :errors="fields.errors.documents"
          attachment-type="DOCUMENT"
          v-model="fields.values.documents"
        />
        <attachment-manager
          :has-controls="false"
          :value="productFamily.documents"
          slot="inherited"
        />
      </inheritance-toggle>
    </fieldset>

    <!-- Selectors -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.selectors')}}</legend>
      <checkbox-group-input
        :collection="availableSelectors"
        :disabled="!isEditable"
        :errors="fields.errors.selectors"
        type="text"
        v-model="fields.values.selectors"
      />
    </fieldset>

    <div class="uk-margin-medium-top">
      <submit-button :disabled="!isSubmittable" type="primary" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import AttachmentInput from '@/components/AttachmentInput';
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import Checkbox from '@/components/Checkbox';
import DateTimePicker from '@/components/DateTimePicker';
import EnergyEfficiencySelect from '@/components/EnergyEfficiencySelect';
import Form from '@/mixins/Form';
import InheritanceToggle from '@/components/InheritanceToggle';
import LegalTextManager from '@/components/LegalTextManager';
import ProductCategoryAssignmentManager
  from '@/components/ProductCategoryAssignmentManager';
import ProductCategoryAssignmentTable
  from '@/components/ProductCategoryAssignmentTable';
import ReadonlyFormField from '@/components/ReadonlyFormField';
import SeparatedList from '@/components/SeparatedList';
import SubmitButton from '@/components/SubmitButton';
import TextInput from '@/components/TextInput';
import TextareaInput from '@/components/TextareaInput';
import TextListInput from '@/components/TextListInput';
import camelCase from 'lodash/camelCase';
import CheckboxGroupInput from '@/components/CheckboxGroupInput';


const AVAILABLE_SELECTORS = ['COLOR', 'SIZE', 'MEMORY', 'STORAGE'];

export default {
  name: 'product-family-revision-form',
  mixins: [
    Form({
      altName: true,
      categories: { type: Form.Types.ReferenceList, default: null },
      description: true,
      documents: { type: Form.Types.ReferenceList, default: null },
      manufacturerContact: true,
      manufacturerContactEu: true,
      legalTexts: { type: Form.Types.ReferenceList, default: null },
      summaryTexts: { type: Form.Types.NullableArray },
      usps: { type: Form.Types.NullableArray },
      validFrom: true,
      validUntil: true,
      selectors: { type: Form.Types.Array },
    }),
  ],
  components: {
    AttachmentInput,
    AttachmentManager,
    CancelButton,
    Checkbox,
    DateTimePicker,
    EnergyEfficiencySelect,
    InheritanceToggle,
    ProductCategoryAssignmentManager,
    ProductCategoryAssignmentTable,
    LegalTextManager,
    ReadonlyFormField,
    SeparatedList,
    SubmitButton,
    TextInput,
    TextareaInput,
    TextListInput,
    CheckboxGroupInput,
  },
  props: {
    productFamily: {
      type: Object,
      required: true,
    },
  },
  computed: {
    availableSelectors() {
      return AVAILABLE_SELECTORS.map(selector => (
        {
          label: this.$t(`fields.${camelCase(selector)}`),
          value: selector,
        }
      ));
    },
  },
};
</script>



// WEBPACK FOOTER //
// ProductFamilyRevisionForm.vue?18752412